import { FormEvent, useEffect, useState } from 'react'
import { loadNovaLimaStatus, lookupNovaLimaMatricula, normalizeMatricula, type NovaLimaLookup, type NovaLimaStatus } from '../../lib/novaLimaMatriculas'

export function NovaLimaMatriculas() {
  const [status, setStatus] = useState<NovaLimaStatus | null>(null)
  const [matricula, setMatricula] = useState('')
  const [result, setResult] = useState<NovaLimaLookup | null>(null)
  const [loadingStatus, setLoadingStatus] = useState(true)
  const [searching, setSearching] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    let mounted = true
    loadNovaLimaStatus()
      .then((loaded) => { if (mounted) setStatus(loaded) })
      .catch((err) => { if (mounted) setError(err instanceof Error ? err.message : 'Não foi possível carregar a base vigente.') })
      .finally(() => { if (mounted) setLoadingStatus(false) })
    return () => { mounted = false }
  }, [])

  async function submit(event: FormEvent) {
    event.preventDefault()
    const normalized = normalizeMatricula(matricula)
    if (!normalized) {
      setError('Informe uma matrícula válida usando somente números.')
      setResult(null)
      return
    }

    setSearching(true)
    setError('')
    setResult(null)
    try {
      const found = await lookupNovaLimaMatricula(normalized)
      setMatricula(normalized)
      setResult(found)
      setStatus({
        base_id: found.base_id,
        row_count: found.row_count,
        imported_at: found.imported_at,
        has_active_base: found.has_active_base,
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível consultar a matrícula.')
    } finally {
      setSearching(false)
    }
  }

  function clear() {
    setMatricula('')
    setResult(null)
    setError('')
  }

  return (
    <section className="operational-tool nova-lima-tool">
      <div className="tool-heading">
        <div>
          <p className="eyebrow">CONSULTA OPERACIONAL</p>
          <h2>Nova Lima e Região - Matrículas</h2>
          <p className="muted">Consulte se a matrícula consta na base vigente de cobrança de terra na contratação do esgoto.</p>
        </div>
        <span className="status-chip">Dados online</span>
      </div>

      <div className="nova-lima-layout">
        <section className="tool-card nova-lima-query-card">
          <form onSubmit={submit}>
            <label className="nova-lima-field">
              <span>Matrícula</span>
              <input
                inputMode="numeric"
                autoComplete="off"
                value={matricula}
                onChange={(event) => {
                  setMatricula(event.target.value.replace(/\D/g, ''))
                  setResult(null)
                  setError('')
                }}
                placeholder="Ex.: 17863609"
                autoFocus
              />
            </label>
            <div className="tool-actions">
              <button type="submit" className="primary-button compact" disabled={searching || loadingStatus}>
                {searching ? 'Consultando...' : 'Consultar'}
              </button>
              {(matricula || result) && <button type="button" className="secondary-button compact" onClick={clear}>Limpar</button>}
            </div>
          </form>

          {loadingStatus && <div className="tool-status info">Carregando base vigente...</div>}
          {!loadingStatus && status && !status.has_active_base && <div className="tool-status warning">Ainda não existe uma base vigente publicada para esta consulta.</div>}
          {error && <div className="tool-status error" role="alert">{error}</div>}

          {result && result.has_active_base && (
            <div className={`nova-lima-result ${result.encontrada ? 'is-found' : 'is-clear'}`} aria-live="polite">
              <span className="nova-lima-result__icon">{result.encontrada ? '✓' : '!'}</span>
              <div>
                <p className="eyebrow">{result.encontrada ? 'MATRÍCULA LOCALIZADA' : 'MATRÍCULA NÃO LOCALIZADA'}</p>
                <h3>{result.matricula}</h3>
                {result.encontrada ? (
                  <p>Esta matrícula consta na base vigente de Nova Lima e Região. <strong>Siga com a cobrança de terra na contratação do esgoto.</strong></p>
                ) : (
                  <p>Esta matrícula não consta na base vigente de Nova Lima e Região.</p>
                )}
              </div>
            </div>
          )}
        </section>

        <aside className="tool-card nova-lima-base-card">
          <p className="eyebrow">BASE VIGENTE</p>
          <strong>{status?.has_active_base ? status.row_count.toLocaleString('pt-BR') : '—'}</strong>
          <span>{status?.has_active_base ? 'matrículas disponíveis para consulta' : 'aguardando publicação da base'}</span>
          <div className="nova-lima-base-meta">
            <small>Última atualização</small>
            <b>{status?.imported_at ? new Date(status.imported_at).toLocaleString('pt-BR') : '—'}</b>
          </div>
          <p className="muted">A consulta usa somente a base atualmente ativa no Suite.</p>
        </aside>
      </div>
    </section>
  )
}
